import api from '@/lib/api';

export interface PinnedTicket {
  id: string;              // Pin-ID (nicht die Ticket-ID)
  ticket_id: string;
  title: string;           // Pin-eigener Titel — umbenennbar, unabhängig vom Ticket
  created_at: string;
  ticket_number: string;
  ticket_subject: string;  // aktueller Betreff des Tickets (nur zur Anzeige/Suche)
  status?: string;
  priority?: string;
}

export interface PinnedTicketsPage {
  items: PinnedTicket[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const PINNED_PAGE_SIZE = 10;

export const pinnedTicketsService = {
  /** Seitenweise, neueste Pins zuerst. `q` filtert Titel, Ticketnummer und Betreff. */
  async list(params: { limit?: number; offset?: number; q?: string } = {}): Promise<PinnedTicketsPage> {
    const search = new URLSearchParams();
    search.set('limit', String(params.limit ?? PINNED_PAGE_SIZE));
    search.set('offset', String(params.offset ?? 0));
    if (params.q?.trim()) search.set('q', params.q.trim());

    const res = await api.get(`/pinned-tickets?${search.toString()}`);
    return (
      res.data?.data ?? { items: [], total: 0, limit: PINNED_PAGE_SIZE, offset: 0, hasMore: false }
    );
  },

  /** Pin-Status eines einzelnen Tickets — für den Button in der Ticketansicht. */
  async check(ticketId: string): Promise<{ pinned: boolean; pin: { id: string; title: string } | null }> {
    const res = await api.get(`/pinned-tickets/check/${ticketId}`);
    return res.data?.data ?? { pinned: false, pin: null };
  },

  /** Anpinnen. Der Titel wird serverseitig aus dem aktuellen Ticket-Betreff übernommen. */
  async pin(ticketId: string): Promise<PinnedTicket> {
    const res = await api.post('/pinned-tickets', { ticket_id: ticketId });
    return res.data?.data;
  },

  /** Benennt NUR den Pin um — der Ticket-Betreff bleibt unverändert. */
  async rename(pinId: string, title: string): Promise<PinnedTicket> {
    const res = await api.patch(`/pinned-tickets/${pinId}`, { title });
    return res.data?.data;
  },

  async unpin(pinId: string): Promise<void> {
    await api.delete(`/pinned-tickets/${pinId}`);
  },

  async unpinByTicket(ticketId: string): Promise<void> {
    await api.delete(`/pinned-tickets/by-ticket/${ticketId}`);
  },
};

export default pinnedTicketsService;
